import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { fmtDate, inr } from "@/lib/format";
import { StatusChip, riderStatusTone } from "@/components/StatusChip";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Search } from "lucide-react";

export default function Riders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [riders, setRiders] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(searchParams.get("status") || "all");

  const load = useCallback(() => {
    const params = {};
    if (search) params.search = search;
    if (status !== "all") params.status = status;
    api.get("/riders", { params }).then((r) => setRiders(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [search, status]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const setRiderStatus = async (r, s) => {
    try {
      await api.patch(`/riders/${r.id}/status`, { status: s });
      toast.success(`${r.name} → ${s}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5" data-testid="riders-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Riders</h1>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input data-testid="rider-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, mobile or rider ID" className="pl-9 bg-card border-border" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-48 bg-card border-border" data-testid="rider-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Swap Blocked">Swap Blocked</SelectItem>
            <SelectItem value="Offboarded">Offboarded</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="card-tactical overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-border">
            <th className="p-3">Rider</th><th className="p-3">Mobile</th><th className="p-3">Area</th>
            <th className="p-3">Payment Day</th><th className="p-3">Deposit</th><th className="p-3">Vehicle</th><th className="p-3">Battery</th>
            <th className="p-3">Onboarded</th><th className="p-3">Status</th><th className="p-3">Payment</th>
            <th className="p-3">Ledger</th>
            {user.role === "admin" && <th className="p-3">Actions</th>}
          </tr></thead>
          <tbody>
            {riders.map((r) => (
              <tr key={r.rider_id} className="border-b border-border/50 hover:bg-slate-50" data-testid={`rider-row-${r.rider_id}`}>
                <td className="p-3 font-medium">{r.name}<div className="text-xs text-slate-500 font-mono">{r.rider_id}</div></td>
                <td className="p-3 font-mono">{r.mobile}</td>
                <td className="p-3 text-slate-400">{r.area || "—"}</td>
                <td className="p-3 text-slate-400">{r.payment_day}</td>
                <td className="p-3">
                  <StatusChip label={`${inr(r.deposit_amount)} ${r.deposit_status}`}
                    tone={r.deposit_status === "Paid" ? "paid" : r.deposit_status === "Refunded" ? "neutral" : "pending"}
                    testid={`rider-deposit-${r.rider_id}`} />
                </td>
                <td className="p-3 font-mono text-xs">{r.vehicle_id || "—"}</td>
                <td className="p-3 font-mono text-xs">{r.battery_id || "—"}</td>
                <td className="p-3 text-slate-400">{fmtDate(r.date_onboarded)}</td>
                <td className="p-3"><StatusChip label={r.status} tone={riderStatusTone(r.status)} testid={`rider-status-${r.rider_id}`} /></td>
                <td className="p-3"><StatusChip label={r.payment.chip} tone={r.payment.tone} testid={`rider-payment-${r.rider_id}`} /></td>
                <td className="p-3">
                  <Button size="sm" variant="outline" className="h-7 text-xs border-border"
                    data-testid={`ledger-${r.rider_id}`} onClick={() => navigate(`/riders/${r.id}`)}>Ledger</Button>
                </td>
                {user.role === "admin" && (
                  <td className="p-3">
                    <div className="flex gap-1.5">
                      {r.status === "Active" && (
                        <Button size="sm" variant="outline" className="h-7 text-xs border-border"
                          data-testid={`offboard-${r.rider_id}`} onClick={() => setRiderStatus(r, "Offboarded")}>Offboard</Button>
                      )}
                      {r.status === "Swap Blocked" && (
                        <Button size="sm" variant="outline" className="h-7 text-xs border-border"
                          data-testid={`unblock-${r.rider_id}`} onClick={() => setRiderStatus(r, "Active")}>Unblock</Button>
                      )}
                      {r.status === "Offboarded" && (
                        <Button size="sm" variant="outline" className="h-7 text-xs border-border"
                          data-testid={`reactivate-${r.rider_id}`} onClick={() => setRiderStatus(r, "Active")}>Reactivate</Button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {riders.length === 0 && (
              <tr><td colSpan={12} className="p-8 text-center text-slate-500" data-testid="riders-empty">No riders found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
